<script lang="ts" setup>
    import '../../../css/choice.css';
    import { onMounted, ref, reactive, computed } from 'vue';
    import { initPinia } from '../../lib/PiniaMgr';
    import { usePrivilegesStore } from '../../lib/Stores/PrivilegesStore' 
    import { IToolbar, ToolbarStyle, ToolbarSize, ToolbarBackground } from "../../lib/Toolbar"
    import { ICol, RenderModeType, IRowSelection, calculateGridRowCount } from "../../lib/ChoiceTableHelpers";
    import { LayoutOrientation, TableRowHighlight, TableRowType, IconTypes, ButtonDisplayStyle, DisplayStyle } from "../../Enums/UiEnums";
    import { PrivilegeType, AppScreens } from "../../Enums/ChoiceEnums";
    import { CheckInOutDashboardTab, ICheckInOutDashboardFilterItem, ICheckInOutFilterAppointmentTypeItem, ICheckInOutFilterAppointmentStatusItem, ICheckInOutFilterAppointmentResourceItem, ICheckInOutFilterAppointmentGroupItem} from '../../lib/Models/ScheduleModel';
    import { ModalStyles, popAlert, confirmDialog } from "../../lib/Notification"
    import { ActionButtonStyle } from "../../lib/Types/Types";
    import { ButtonDisplaySize } from "../../Enums/UiEnums";
    import ResizeMonitor from '../../components/ResizeMonitor.vue'
    import ChoiceTable from "../../components/ChoiceTable.vue";
    import Toolbar from "../../components/Toolbar.vue"
    import TextInput from '../../components/TextInput.vue';
    import Checkbox from '../../components/Inputs/Checkbox.vue';
    import Icon from '../../components/Icon.vue'
    import Tab from "../../components/Tab.vue"
    import MessageBadge from '../../components/Messages/MessageBadge.vue';
    import ActionButtonV2 from '../../components/ActionButtonV2.vue'; 
    import * as ChoiceApi from '../../lib/ChoiceApi';

    const emit = defineEmits(["changeToFilter"]);

    const gridRows = ref<number>();
    const filterTable = ref();
    const privilegesStore = usePrivilegesStore(initPinia());
    const tableHeight = 330;

    const props = defineProps<{
        tabId: CheckInOutDashboardTab
    }>();

    enum filterTab {
        details,
        appointmentType,
        appointmentStatus,
        resource,
        group
    }

    enum actionToolbar {
        new,
        copy,
        delete
    }

    const toolbarConfig = reactive<IToolbar>({
        toolbarList: [
            {identifier: actionToolbar.new, label: "New", tooltip: "Create a new filter.", action: ActionButtonStyle.New, performAction: () => newFilter()},
            {identifier: actionToolbar.copy, label: "Copy", tooltip: "Copy the selected filter and mark as new.", action: ActionButtonStyle.Add, enabled: false, performAction: () => copyFilter()},
            {identifier: actionToolbar.delete, label: "Delete", tooltip: "Delete the selected filter", action: ActionButtonStyle.Delete, enabled: false, performAction: () => deleteFilter()},
        ]
    }); 

    const formData = reactive<{
        tableData: ICheckInOutDashboardFilterItem[],
        appointmentTypeList: ICheckInOutFilterAppointmentTypeItem[],
        appointmentStatusList: ICheckInOutFilterAppointmentStatusItem[],
        appointmentResourceList: ICheckInOutFilterAppointmentResourceItem[],
        appointmentGroupList: ICheckInOutFilterAppointmentGroupItem[],
        toolbarExecute: boolean,
        filterTabId?: filterTab,
        currentRow: IRowSelection & ICheckInOutDashboardFilterItem,
        filterId?: number,
        description: string,
        tooltip?: string,
        active: boolean,
        shared: boolean,
        wasShared: boolean,
        defaultFilter: boolean,
        saved: boolean
    }>({
        tableData: [],
        appointmentTypeList: [],
        appointmentStatusList: [],
        appointmentResourceList: [],
        appointmentGroupList: [],
        toolbarExecute: false,
        filterTabId: filterTab.details,
        currentRow: getNewCurrentRow(),
        description: "",
        active: true,
        shared: false,
        wasShared: false,
        defaultFilter: false,
        saved: false
    });

    interface IFilterTabItem {
        filterTabId: filterTab,
        title: string
    }

    const tabList: IFilterTabItem[]  = [
        {filterTabId: filterTab.details, title: "Details"},
        {filterTabId: filterTab.appointmentType, title: "Type"},
        {filterTabId: filterTab.appointmentStatus, title: "Status"},
        {filterTabId: filterTab.resource, title: "Resource"},
        {filterTabId: filterTab.group, title: "Group"},
    ];

    const filterColumns = ref<ICol[]>([
        { Name: 'Description', DataField: 'description' },
        { Name: 'Shared', DataField: 'shared' },
        { Name: 'Active', DataField: 'active' },
    ]);

    const appointemntTypeColumns = ref<ICol[]>([
        { Name: '', DataField: 'selected', RenderMode: RenderModeType.Selection },
        { Name: 'Appointment Type', DataField: 'appointmentName' }
    ]);
    const appointemntStatusColumns = ref<ICol[]>([
        { Name: '', DataField: 'selected', RenderMode: RenderModeType.Selection },
        { Name: 'Appointment Status', DataField: 'appointmentStatusDescription' }
    ]);
    const appointmentResourceColumns = ref<ICol[]>([
        { Name: '', DataField: 'selected', RenderMode: RenderModeType.Selection },
        { Name: 'Resource', DataField: 'resourceName' },
        { Name: 'Category', DataField: 'resourceCategory' },
    ]);
    const appointmentGroupColumns = ref<ICol[]>([
        { Name: '', DataField: 'selected', RenderMode: RenderModeType.Selection },
        { Name: 'Group', DataField: 'groupName' }
    ]);

    async function fetchData(){
        const promisePrivilege = privilegesStore.getScreens([AppScreens.CheckInOutDashboard, AppScreens.CheckInOutDashboardShared]);
        const promiseFilterList = refreshFilterList();
        const promiseTypeList =  refreshTypeList();
        await Promise.all([promisePrivilege, promiseFilterList, promiseTypeList]);
    }
    fetchData();

    onMounted(async () => {
        windowResize();
    });

    function windowResize() {
        gridRows.value = calculateGridRowCount(240);
    }

    const sharedEnabled = computed<boolean>(() => {
        if (privilegesStore.screens[AppScreens.CheckInOutDashboardShared] < PrivilegeType.ReadUpdateWrite)
            return false;
        if (!formData.currentRow)
            return false;
        if (formData.wasShared && formData.currentRow.filterId > 0)
            return false;
        return true;
    });

    const saveEnabled = computed<boolean>(() => {
        if (!privilegesStore.screensWithUpdatePrivileges[AppScreens.CheckInOutDashboard])
            return false;
        if (!privilegesStore.screensWithWritePrivileges[AppScreens.CheckInOutDashboardShared] && formData.shared)
            return false;
        return true;        
    });

    function enableCopy(enabled: boolean) {
        if (!privilegesStore.screensWithUpdatePrivileges[AppScreens.CheckInOutDashboard])
            return false;
        return enabled;
    }

    function enableDelete(enabled: boolean) {
        if (!privilegesStore.screensWithUpdatePrivileges[AppScreens.CheckInOutDashboard])
            return false;
        if (!privilegesStore.screensWithWritePrivileges[AppScreens.CheckInOutDashboardShared] && formData.shared)
            return false;
        return enabled;
    }

    async function refreshFilterList() {
        formData.tableData = await ChoiceApi.schedule.DashboardFilterList(props.tabId) ?? [];
    }

    async function onRowClick(rowData: IRowSelection,  _rowType: TableRowType, _evt: MouseEvent) : Promise<void> {
        formData.currentRow = <ICheckInOutDashboardFilterItem> rowData;
        setFormDataFromCurrentRow();
        enableToolbar(true);
        refreshTypeList();
    }

    function getNewCurrentRow() {
        return {filterId: -1, description: "New", tooltip: "", active: true, shared: false, defaultFilter: false};
    }

    function setFormDataFromCurrentRow() {
        formData.filterId = formData.currentRow.filterId;
        formData.description = formData.currentRow.description;
        formData.tooltip = formData.currentRow.tooltip;
        formData.active = formData.currentRow.active;
        formData.shared = formData.currentRow.shared;
        formData.wasShared = formData.currentRow.shared;
        formData.defaultFilter = formData.currentRow.defaultFilter;
    }

    async function refreshTypeList() {
        const result = await ChoiceApi.schedule.DashboardTypeList(formData.filterId ?? -1) ?? [];
        formData.appointmentTypeList = result.typeList;
        formData.appointmentStatusList = result.statusList;
        formData.appointmentResourceList = result.resourceList;
        formData.appointmentGroupList = result.groupList;  
    }

    async function newFilter() {
        formData.currentRow = {filterId: -1, description: "New", tooltip: "", active: true, shared: false, defaultFilter: false};
        formData.filterId = -1;
        setFormDataFromCurrentRow();
        formData.filterTabId = filterTab.details;
        enableToolbar(false);
        refreshTypeList();
    }

    function enableToolbar(enabled: boolean) {
        toolbarConfig.toolbarList[actionToolbar.copy].enabled = enableCopy(enabled);
        toolbarConfig.toolbarList[actionToolbar.delete].enabled = enableDelete(enabled);
    }

    async function saveFilter(isCopy: boolean) {

        if (formData.description.length == 0) {
            popAlert("Please enter a description.", "Save", ModalStyles.info);
            return;
        }

        formData.saved = false;
        const selectedAppointmentType = formData.appointmentTypeList.filter(o => o.selected);
        const selectedAppointmentStatus = formData.appointmentStatusList.filter(o => o.selected);
        const selectedAppointmentResource = formData.appointmentResourceList.filter(o => o.selected);
        const selectedAppointmentGroup = formData.appointmentGroupList.filter(o => o.selected);

        const post_data = {
            tabId: props.tabId,
            filterId: formData.filterId,
            description: formData.description,
            tooltip: formData.tooltip,
            active: formData.active,
            shared: formData.shared,
            defaultFilter: formData.defaultFilter,
            isCopy: isCopy,
            typeList: selectedAppointmentType.map(o => o.appointmentTypeId),
            statusList: selectedAppointmentStatus.map(o => o.appointmentStatusId),
            resourceList: selectedAppointmentResource.map(o => o.resourceId),
            groupList: selectedAppointmentGroup.map(function(o){return {"groupId": o.groupId, "groupTypeId": o.groupIdType}}),
        }
        const result = await ChoiceApi.schedule.DashboardFilterSave(post_data);
        if (!result.passed) {
            popAlert(result.errorMessage ?? `Failed to ${isCopy ? "copy" : "save"} the filter settings`, isCopy ? "Copy Filter": "Save Filter", ModalStyles.warning);
            return;
        }
        if (result.filterId) {
            if ((formData.filterId ?? -1) < 0) {
                formData.currentRow = getNewCurrentRow();
                formData.defaultFilter = false;
            }
            formData.filterId = result.filterId;
            formData.currentRow.filterId = result.filterId;
            formData.currentRow.description = formData.description;
            formData.currentRow.tooltip = formData.tooltip;
            formData.currentRow.active = formData.active;
            formData.currentRow.shared = formData.shared;
            formData.currentRow.defaultFilter = formData.defaultFilter;
            formData.wasShared = formData.shared;
            if (isCopy && !sharedEnabled.value) {
                formData.currentRow.shared = false;
                formData.shared = false;
            }
            // Clear the current default filter if a new one
            if (formData.defaultFilter) {
                formData.tableData.forEach(item => item.defaultFilter = false);
            }
            formData.currentRow.defaultFilter = formData.defaultFilter;
            emit("changeToFilter");
            formData.saved = true;
            await refreshFilterList();
            filterTable.value.setSelectedRow(formData.currentRow.filterId.toString());
            enableToolbar(true);
        }
        if (result.errorMessage) {
            popAlert(result.errorMessage, isCopy ? "Copy Filter": "Save Filter", ModalStyles.info);
        }
    }
    async function copyFilter() {
        if (formData.currentRow.filterId <= 0) {
            popAlert("Please select a filter to copy.", "Copy Filter", ModalStyles.info);
            return;
        }
        formData.filterId = -1;
        formData.description = `${formData.description.substring(0,45)} Copy`;
        await saveFilter(true);
    }
    async function deleteFilter() {
        if (formData.currentRow.filterId <= 0) {
            popAlert("Please select a filter to remove.", "Delete Filter", ModalStyles.info);
            return;
        }

        if (formData.shared) {
            const result = await confirmDialog("Deleting the selected filter will delete it for ALL USER's.  Do you wish to continue?", { RejectText: "Cancel", ConfirmText: "Continue", titleHTML: "Delete Filter", modalStyle: ModalStyles.danger});  
            if (!result) {
                popAlert("If you wish to remove a shared filter from your dashboard list just uncheck the active button.  This will only remove it for you.", "Another Option", ModalStyles.info);
                return;
            }
        }
        else {
            const result = await confirmDialog("Are you sure you wish to delete the selected filter?", { RejectText: "Cancel", ConfirmText: "Continue", titleHTML: "Delete Filter"});  
            if (!result)
                return;
        }

        const result = await ChoiceApi.schedule.DashboardDeleteFilter(formData.filterId ?? -1);
        if (!result.passed) {
            popAlert("Unable to remove filter.", "Delete Filter", ModalStyles.warning);
            return;
        }

        const index = formData.tableData.findIndex(element => element.filterId == formData.currentRow.filterId);
        if (index > -1) {
            formData.tableData.splice(index, 1);
        }
        emit("changeToFilter");
        newFilter();
    }

    function hasSelectedItems(tab: IFilterTabItem) {
        switch (tab.filterTabId) {
            case filterTab.appointmentType:
                return formData.appointmentTypeList.some(item => item.selected);
            case filterTab.appointmentStatus:
                return formData.appointmentStatusList.some(item => item.selected);
            case filterTab.resource:
                return formData.appointmentResourceList.some(item => item.selected);
            case filterTab.group:
                return formData.appointmentGroupList.some(item => item.selected);
        }
        return false;
    }
</script>

<style scoped>
    .fixedTabWidth {
        width: 100px;
    }
</style>

<template>
    <ResizeMonitor @resize="windowResize" />
    <div class="row">
        <div class="col-5">
            <div class="row">
                <div class="col">
                    <ChoiceTable
                        ref="filterTable"  
                        :columns="filterColumns" 
                        :data="formData.tableData" 
                        :show-filters="false" 
                        :allowSorting="false"
                        fontSize="smaller"
                        :rowHighlight="TableRowHighlight.onSingleClick"
                        :height="400"
                        @rowclick="onRowClick"
                        :key-value-data-member="'filterId'">
                        <template #cell="{col, row, value}">
                            <Icon v-if="'Shared' == col.Name && value"  :icon="IconTypes.CheckMark"/>
                            <span v-if="'Shared' == col.Name && !value"></span>
                            <Icon v-if="'Active' == col.Name && value"  :icon="IconTypes.CheckMark"/>
                            <span v-if="'Active' == col.Name && !value"></span>
                            <span v-if="'Description' == col.Name">{{ value }}</span>
                            <MessageBadge class="ms-1" v-if="'Description' == col.Name && row.defaultFilter" :displayStyle="DisplayStyle.Primary">
                                Default
                            </MessageBadge>
                        </template>
                    </ChoiceTable>
                </div>
            </div>
        </div>
        <div class="col-7 mt-0">
            <div class="row">
                <div class="col ms-2 me-2 border-bottom bg-primary text-white">
                    {{ formData.currentRow.description }}
                </div>
            </div>
            <div class="row">
                <div class="col">
                    <template v-for="item in tabList">
                        <Tab :value="item.filterTabId" v-model="formData.filterTabId" :layout="LayoutOrientation.Horz"> 
                            {{ item.title }}
                            <template v-slot:marker v-if="hasSelectedItems(item)">
                                <Icon 
                                    :icon="IconTypes.CheckMark" 
                                    :displayStyle="DisplayStyle.Success" 
                                    :class="'text-isalus-primary label-isalus-small'"
                                    :title="'Items need attention'"/>                            
                            </template>                   
                        </Tab>
                    </template>                        
                </div>
            </div>
            <div class="row mt-2">
                <div v-if="formData.filterTabId == filterTab.details" class="col">
                    <div class="row mt-2">
                        <div class="col isalus-section-header label-isalus-small">
                            Description
                        </div>
                    </div>
                    <div class="row mt-1">
                        <div class="col">
                            <TextInput 
                                v-model:textValue="formData.description" 
                                :cssFramework="'vanilla'"  
                                :maxLength="50"
                                placeholder="Please enter a description" />
                        </div>
                    </div>
                    <div class="row mt-2">
                        <div class="col isalus-section-header label-isalus-small">
                            Tooltip
                        </div>
                    </div>
                    <div class="row mt-1">
                        <div class="col">
                            <TextInput 
                                v-model:textValue="formData.tooltip" 
                                :cssFramework="'vanilla'"  
                                :maxLength="200"
                                placeholder="Please enter a tooltip" />
                        </div>
                    </div>
                    <div class="row mt-2">
                        <div class="col">
                            <Checkbox v-model="formData.active" :title="'This filter will appear/not appear in your filter list on the dashboard if checked/unchecked.'"/>                            
                            <span label class="ps-1 pe-3 label-isalus-small">Active</span>               
                            <Checkbox 
                                v-model="formData.shared" 
                                :title="'Check to make filter shareable'"
                                :disabled="!sharedEnabled"/>                            
                            <span label class="ps-1 pe-3 label-isalus-small">Shared</span>               
                            <Checkbox v-model="formData.defaultFilter" :title="'Check to make this filter my default filter'"/>                            
                            <span label class="ps-1 pe-3 label-isalus-small">My Default Filter</span>               
                        </div>
                    </div>                        
                </div>
                <div v-if="formData.filterTabId == filterTab.appointmentType" class="col">
                    <ChoiceTable 
                        :columns="appointemntTypeColumns" 
                        :data="formData.appointmentTypeList" 
                        :show-filters="false" 
                        :allowSorting="false"
                        fontSize="smaller"
                        :height="tableHeight"
                        :checkBoxScale="1.2"
                        :key-value-data-member="'appointmentTypeId'">
                    </ChoiceTable>
                </div>              
                <div v-if="formData.filterTabId == filterTab.appointmentStatus" class="col">
                    <ChoiceTable 
                        :columns="appointemntStatusColumns" 
                        :data="formData.appointmentStatusList" 
                        :show-filters="false" 
                        :allowSorting="false"
                        fontSize="smaller"
                        :height="tableHeight"
                        :checkBoxScale="1.2"
                        :key-value-data-member="'appointmentStatusId'">
                    </ChoiceTable>
                </div>              
                <div v-if="formData.filterTabId == filterTab.resource" class="col">
                    <ChoiceTable 
                        :columns="appointmentResourceColumns" 
                        :data="formData.appointmentResourceList" 
                        :show-filters="false" 
                        :allowSorting="false"
                        fontSize="smaller"
                        :height="tableHeight"
                        :checkBoxScale="1.2"
                        :key-value-data-member="'resourceId'">
                    </ChoiceTable>                        
                </div>              
                <div v-if="formData.filterTabId == filterTab.group" class="col">
                    <ChoiceTable 
                        :columns="appointmentGroupColumns" 
                        :data="formData.appointmentGroupList" 
                        :show-filters="false" 
                        :allowSorting="false"
                        fontSize="smaller"
                        :height="tableHeight"
                        :checkBoxScale="1.2"
                        :key-value-data-member="'groupId'">
                    </ChoiceTable>                        
                </div>              
            </div>
        </div>
    </div>
    <div class="row">
        <div class="col-5">
            <Toolbar 
                :configuration="toolbarConfig" 
                :toolbarSize="ToolbarSize.regular" 
                :toolbarStyle="ToolbarStyle.outline" 
                :toolbarBackground="ToolbarBackground.white"
                :spin="formData.toolbarExecute"/>
        </div>
        <div class="col text-end mt-2">
            <ActionButtonV2 
                :action="ActionButtonStyle.Save"
                :text="'Save Settings'"
                :size="ButtonDisplaySize.Small" 
                :spinText="'Saving'"
                :enabled="saveEnabled"
                :displayStyle="ButtonDisplayStyle.OutlinePrimary"  
                :performAction="() => saveFilter(false)" />
        </div>
    </div>
</template>

